import type { ModelId } from "@/lib/ai/models";

export type AiOption = "AI_STANDARD" | "AI_PRO" | "AI_MAX";

export type AiModelAccess = {
  isResolved: boolean;
  options: string[];
  restrictedModelIds: ModelId[];
  hasAiStandard: boolean;
  hasAiPro: boolean;
  hasAiMax: boolean;
  currentOption: AiOption | null;
  canUseHighTierModels: boolean;
};

const AI_STANDARD_OPTION = "AI_STANDARD";
const AI_PRO_OPTION = "AI_PRO";
const AI_MAX_OPTION = "AI_MAX";

const normalizeAiOptions = (options: readonly string[] | null | undefined) => {
  if (!options || options.length === 0) {
    return [] as string[];
  }
  const normalized = options
    .map((option) => option.trim().toUpperCase())
    .filter((option) => option.length > 0);
  return Array.from(new Set(normalized));
};

/**
 * Resolves the highest AI plan present in the given contract options.
 */
export function resolveAiCurrentOption(
  options: readonly string[] | null | undefined,
): AiOption | null {
  const normalizedOptions = normalizeAiOptions(options);
  if (normalizedOptions.includes(AI_MAX_OPTION)) {
    return AI_MAX_OPTION;
  }
  if (normalizedOptions.includes(AI_PRO_OPTION)) {
    return AI_PRO_OPTION;
  }
  if (normalizedOptions.includes(AI_STANDARD_OPTION)) {
    return AI_STANDARD_OPTION;
  }
  return null;
}

export function resolveAiModelAccess(
  options: readonly string[] | null | undefined,
  isResolved: boolean,
  restrictedModelIds: readonly ModelId[],
): AiModelAccess {
  const normalizedOptions = normalizeAiOptions(options);
  const normalizedRestrictedModelIds = Array.from(
    new Set(restrictedModelIds),
  ) as ModelId[];
  const hasAiStandard = normalizedOptions.includes(AI_STANDARD_OPTION);
  const hasAiPro = normalizedOptions.includes(AI_PRO_OPTION);
  const hasAiMax = normalizedOptions.includes(AI_MAX_OPTION);
  const currentOption = resolveAiCurrentOption(normalizedOptions);

  return {
    isResolved,
    options: normalizedOptions,
    restrictedModelIds: normalizedRestrictedModelIds,
    hasAiStandard,
    hasAiPro,
    hasAiMax,
    currentOption,
    canUseHighTierModels: hasAiPro || hasAiMax,
  };
}

export function isRestrictedHighTierModel(
  modelId: ModelId,
  access: Pick<AiModelAccess, "isResolved" | "restrictedModelIds">,
) {
  return access.isResolved && access.restrictedModelIds.includes(modelId);
}
